/**
 * Processing Jobs Page
 *
 * Monitors and displays feed import pipeline status with clear progress visualization.
 * Shows pipeline stages (enrichment → benefit scoring → embeddings → AEO) with product counts.
 * Allows Content Managers and Super Admins to manage failed/stuck batches.
 *
 * Part of Story: Multi-Provider Processing Mode for Feed Imports
 * Date: 2026-03-06
 * Updated: 2026-03-09 - Added failed batch management and cancel capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Activity,
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
  Sparkles,
  Users,
  Database,
  FileText,
  AlertTriangle,
  TrendingUp,
  RotateCcw,
  StopCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useSite } from '@/contexts/SiteContext';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import { formatDistanceToNow } from 'date-fns';

interface PipelineStage {
  name: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  completed: number;
  inProgress: number;
  total: number;
  batchesProcessing: number;
  oldestBatchAge?: string;
}

interface PipelineStatus {
  totalProducts: number;
  activeRunProducts?: number;
  stages: {
    enrichment: { completed: number; failed?: number; inProgress: number; batches: number; oldestStarted?: string };
    benefitScoring: { completed: number; failed?: number; inProgress: number; batches: number; oldestStarted?: string };
    embeddings: { completed: number; failed?: number; inProgress: number; batches: number; oldestStarted?: string; rateLimited?: number };
    aeoGeneration: { completed: number; inProgress: number; batches: number; oldestStarted?: string };
  };
  issues: Array<{
    type: 'stuck' | 'failed' | 'slow';
    message: string;
    count: number;
  }>;
}

interface ActiveRun {
  id: string;
  config_name: string;
  status: string;
  started_at: string;
  summary?: {
    total_products?: number;
    processed?: number;
  };
}

interface FailedBatch {
  id: string;
  batch_number: number;
  current_phase: string;
  status: string;
  error_message: string;
  product_count: number;
  config_name: string;
  run_id: string;
  created_at: string;
  diagnosis?: {
    type: 'orphaned' | 'duplicate' | 'transient' | 'missing_research' | 'billing' | 'unknown';
    label: string;
    description: string;
    action: 'cancel' | 'reset' | 'fix_research' | 'investigate';
    actionLabel: string;
    severity: 'low' | 'medium' | 'high';
    existingProducts: number;
    totalProducts: number;
  };
}

interface DiagnosisSummary {
  count: number;
  action: string;
  label: string;
}

interface FailedBatchesResponse {
  total_failed: number;
  by_diagnosis?: Record<string, DiagnosisSummary>;
  batches: FailedBatch[];
}

const STAGE_CONFIG = {
  enrichment: {
    label: 'AI Enrichment',
    description: 'Extract product attributes using AI',
    icon: <Sparkles className="h-5 w-5" />,
    color: 'text-orange-600',
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-100 dark:bg-orange-900/30',
  },
  benefitScoring: {
    label: 'Benefit Scoring',
    description: 'Calculate perceived benefits for personas',
    icon: <Users className="h-5 w-5" />,
    color: 'text-pink-600',
    bgColor: 'bg-pink-500',
    lightBg: 'bg-pink-100 dark:bg-pink-900/30',
  },
  embeddings: {
    label: 'Embeddings',
    description: 'Generate semantic vectors for search',
    icon: <Database className="h-5 w-5" />,
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-500',
    lightBg: 'bg-cyan-100 dark:bg-cyan-900/30',
  },
  aeoGeneration: {
    label: 'AEO Content',
    description: 'Generate optimized product content',
    icon: <FileText className="h-5 w-5" />,
    color: 'text-violet-600',
    bgColor: 'bg-violet-500',
    lightBg: 'bg-violet-100 dark:bg-violet-900/30',
  },
};

export default function ProcessingJobs() {
  const { selectedSite } = useSite();
  const { user } = useAuth();
  const { toast } = useToast();
  const siteId = selectedSite?.id || selectedSite?._id;

  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [failedBatches, setFailedBatches] = useState<FailedBatchesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFailedBatches, setShowFailedBatches] = useState(false);
  const [selectedBatches, setSelectedBatches] = useState<Set<string>>(new Set());
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch pipeline status
  const fetchPipelineStatus = useCallback(async (showRefreshIndicator = false) => {
    if (!siteId) return;

    if (showRefreshIndicator) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await authFetch(`/feed-imports/pipeline-status?site_id=${siteId}`);
      if (response.ok) {
        const data = await response.json();
        setPipelineStatus(data);
      }
    } catch (error) {
      console.error('Error fetching pipeline status:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [siteId]);

  // Fetch active runs
  const fetchActiveRuns = useCallback(async () => {
    if (!siteId) return;

    try {
      const response = await authFetch(`/feed-imports?site_id=${siteId}`);
      if (response.ok) {
        const configs = await response.json();
        const runs: ActiveRun[] = [];
        for (const config of configs) {
          if (config.latest_run?.status &&
              ['sub_batch_processing', 'enriching', 'embedding', 'aeo_processing', 'processing'].includes(config.latest_run.status)) {
            runs.push({
              id: config.latest_run.id,
              config_name: config.name,
              status: config.latest_run.status,
              started_at: config.latest_run.started_at,
              summary: config.latest_run.summary,
            });
          }
        }
        setActiveRuns(runs);
      }
    } catch (error) {
      console.error('Error fetching active runs:', error);
    }
  }, [siteId]);

  // Fetch failed batches
  const fetchFailedBatches = useCallback(async () => {
    if (!siteId) return;

    try {
      const response = await authFetch(`/feed-imports/failed-sub-batches?site_id=${siteId}`);
      if (response.ok) {
        const data = await response.json();
        setFailedBatches(data);
      }
    } catch (error) {
      console.error('Error fetching failed batches:', error);
    }
  }, [siteId]);

  // Initial load
  useEffect(() => {
    fetchPipelineStatus();
    fetchActiveRuns();
    fetchFailedBatches();
  }, [fetchPipelineStatus, fetchActiveRuns, fetchFailedBatches]);

  // Auto-refresh every 15 seconds when there's active processing
  useEffect(() => {
    if (activeRuns.length === 0 && (!failedBatches || failedBatches.total_failed === 0)) return;

    const interval = setInterval(() => {
      fetchPipelineStatus(true);
      fetchActiveRuns();
      fetchFailedBatches();
    }, 15000);

    return () => clearInterval(interval);
  }, [activeRuns.length, failedBatches, fetchPipelineStatus, fetchActiveRuns, fetchFailedBatches]);

  const handleRefresh = () => {
    fetchPipelineStatus(true);
    fetchActiveRuns();
    fetchFailedBatches();
  };

  // Reset failed batches
  const handleResetFailed = async (resetAll: boolean = false) => {
    setActionLoading(true);
    try {
      const body = resetAll
        ? { site_id: siteId, reset_all: true }
        : { site_id: siteId, sub_batch_ids: Array.from(selectedBatches) };

      const response = await authFetch('/feed-imports/reset-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Batches Reset',
          description: `Successfully reset ${result.reset_count} failed batches for retry.`,
        });
        setSelectedBatches(new Set());
        setResetDialogOpen(false);
        fetchFailedBatches();
        fetchPipelineStatus(true);
      } else {
        const error = await response.json();
        toast({
          title: 'Reset Failed',
          description: error.error || 'Failed to reset batches',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Reset Failed',
        description: 'An error occurred while resetting batches',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel batches - accepts optional array for single-batch actions
  const handleCancelBatches = async (batchIds?: string[]) => {
    const idsToCancel = batchIds || Array.from(selectedBatches);
    if (idsToCancel.length === 0) return;

    setActionLoading(true);
    try {
      const response = await authFetch('/feed-imports/cancel-sub-batches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sub_batch_ids: idsToCancel }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Batches Cancelled',
          description: `Cancelled ${result.cancelled_sub_batches} batches${result.cancelled_openai_batches?.length ? ` and ${result.cancelled_openai_batches.length} OpenAI jobs` : ''}.`,
        });
        setSelectedBatches(new Set());
        setCancelDialogOpen(false);
        fetchFailedBatches();
        fetchPipelineStatus(true);
        fetchActiveRuns();
      } else {
        const error = await response.json();
        toast({
          title: 'Cancel Failed',
          description: error.error || 'Failed to cancel batches',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Cancel Failed',
        description: 'An error occurred while cancelling batches',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Reset/retry batches - accepts optional array for single-batch actions
  const handleResetBatches = async (batchIds?: string[]) => {
    const idsToReset = batchIds || Array.from(selectedBatches);
    if (idsToReset.length === 0) return;

    setActionLoading(true);
    try {
      const response = await authFetch('/feed-imports/reset-failed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSite?.id,
          sub_batch_ids: idsToReset,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: 'Batches Reset',
          description: `Reset ${result.reset_count} batches for retry.`,
        });
        setSelectedBatches(new Set());
        setResetDialogOpen(false);
        fetchFailedBatches();
        fetchPipelineStatus(true);
        fetchActiveRuns();
      } else {
        const error = await response.json();
        toast({
          title: 'Reset Failed',
          description: error.error || 'Failed to reset batches',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Reset Failed',
        description: 'An error occurred while resetting batches',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleBatchSelection = (batchId: string) => {
    const newSelection = new Set(selectedBatches);
    if (newSelection.has(batchId)) {
      newSelection.delete(batchId);
    } else {
      newSelection.add(batchId);
    }
    setSelectedBatches(newSelection);
  };

  const selectAllBatches = () => {
    if (!failedBatches) return;
    const allIds = new Set(failedBatches.batches.map(b => b.id));
    setSelectedBatches(allIds);
  };

  const clearSelection = () => {
    setSelectedBatches(new Set());
  };

  if (!siteId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select a site to view processing status.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const getProgressPercent = (completed: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const formatDuration = (startedAt: string) => {
    return formatDistanceToNow(new Date(startedAt), { addSuffix: false });
  };

  const formatPhase = (phase: string) => {
    return phase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6" />
            Pipeline Status
          </h1>
          <p className="text-muted-foreground mt-1">
            Feed import processing for {selectedSite?.name || 'your site'}
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : pipelineStatus ? (
        <>
          {/* Active Processing Alert */}
          {activeRuns.length > 0 && (
            <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/30">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <AlertTitle className="text-blue-800 dark:text-blue-200">
                Processing Active
              </AlertTitle>
              <AlertDescription className="text-blue-700 dark:text-blue-300">
                {activeRuns.map(run => (
                  <div key={run.id} className="mt-1">
                    <strong>{run.config_name}</strong> - {run.status.replace(/_/g, ' ')}
                    {run.started_at && (
                      <span className="text-sm ml-2">
                        (running for {formatDuration(run.started_at)})
                      </span>
                    )}
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {/* Failed Batches Alert with Diagnosis Summary */}
          {failedBatches && failedBatches.total_failed > 0 && (
            <Alert variant="destructive" className="border-red-200 bg-red-50 dark:bg-red-950/30">
              <XCircle className="h-4 w-4 text-red-600" />
              <AlertTitle className="text-red-800 dark:text-red-200 flex items-center justify-between">
                <span>{failedBatches.total_failed} Failed Batches</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setShowFailedBatches(!showFailedBatches)}
                >
                  {showFailedBatches ? (
                    <>
                      <ChevronUp className="h-3 w-3 mr-1" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-3 w-3 mr-1" />
                      Show Details
                    </>
                  )}
                </Button>
              </AlertTitle>
              <AlertDescription className="text-red-700 dark:text-red-300 mt-2">
                {/* Diagnosis Summary with Recommended Actions */}
                {failedBatches.by_diagnosis ? (
                  <div className="space-y-2">
                    {failedBatches.by_diagnosis.orphaned?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>🗑️ <strong>{failedBatches.by_diagnosis.orphaned.count}</strong> batches: Products deleted from catalog</span>
                        <span className="text-xs font-medium text-red-600">→ Cancel these batches</span>
                      </div>
                    )}
                    {failedBatches.by_diagnosis.duplicate?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>🔄 <strong>{failedBatches.by_diagnosis.duplicate.count}</strong> batches: Already being processed elsewhere</span>
                        <span className="text-xs font-medium text-red-600">→ Cancel these batches</span>
                      </div>
                    )}
                    {failedBatches.by_diagnosis.transient?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>⚡ <strong>{failedBatches.by_diagnosis.transient.count}</strong> batches: Rate limits or network issues</span>
                        <span className="text-xs font-medium text-green-600">→ Retry these batches</span>
                      </div>
                    )}
                    {failedBatches.by_diagnosis.billing?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>💳 <strong>{failedBatches.by_diagnosis.billing.count}</strong> batches: Billing or quota issue</span>
                        <span className="text-xs font-medium text-green-600">→ Fix billing, then retry</span>
                      </div>
                    )}
                    {failedBatches.by_diagnosis.missing_research?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>🔬 <strong>{failedBatches.by_diagnosis.missing_research.count}</strong> batches: Missing category research</span>
                        <span className="text-xs font-medium text-yellow-600">→ Add research, then retry</span>
                      </div>
                    )}
                    {failedBatches.by_diagnosis.unknown?.count > 0 && (
                      <div className="flex items-center justify-between bg-white/50 dark:bg-white/5 rounded px-2 py-1">
                        <span>❓ <strong>{failedBatches.by_diagnosis.unknown.count}</strong> batches: Unknown error</span>
                        <span className="text-xs font-medium text-gray-600">→ Investigate</span>
                      </div>
                    )}
                  </div>
                ) : (
                  // Fallback for old API response format
                  <div>
                    {Object.entries(failedBatches.by_error || {}).slice(0, 3).map(([error, data]: [string, any], idx) => (
                      <div key={idx} className="mt-1 text-sm">
                        • {data.count}x: {error.substring(0, 80)}{error.length > 80 ? '...' : ''}
                      </div>
                    ))}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          {/* Failed Batches Detail Table */}
          {showFailedBatches && failedBatches && failedBatches.total_failed > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Failed Batches</CardTitle>
                  <div className="flex gap-2">
                    {selectedBatches.size > 0 && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={clearSelection}
                        >
                          Clear ({selectedBatches.size})
                        </Button>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setResetDialogOpen(true)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Reset Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setCancelDialogOpen(true)}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Cancel Selected
                        </Button>
                      </>
                    )}
                    {/* Bulk actions by diagnosis */}
                    {failedBatches.by_diagnosis && (
                      <div className="flex gap-2">
                        {(failedBatches.by_diagnosis.orphaned?.count || 0) + (failedBatches.by_diagnosis.duplicate?.count || 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200"
                            onClick={() => {
                              const toCancel = failedBatches.batches
                                .filter(b => b.diagnosis?.action === 'cancel')
                                .map(b => b.id);
                              handleCancelBatches(toCancel);
                            }}
                          >
                            Cancel {(failedBatches.by_diagnosis.orphaned?.count || 0) + (failedBatches.by_diagnosis.duplicate?.count || 0)} Orphaned
                          </Button>
                        )}
                        {(failedBatches.by_diagnosis.transient?.count || 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600 border-green-200"
                            onClick={() => {
                              const toReset = failedBatches.batches
                                .filter(b => b.diagnosis?.action === 'reset')
                                .map(b => b.id);
                              handleResetBatches(toReset);
                            }}
                          >
                            Retry {failedBatches.by_diagnosis.transient?.count || 0} Transient
                          </Button>
                        )}
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={selectAllBatches}
                    >
                      Select All
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="w-20">Batch</TableHead>
                        <TableHead>Diagnosis</TableHead>
                        <TableHead>Products</TableHead>
                        <TableHead>Recommendation</TableHead>
                        <TableHead className="w-24">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {failedBatches.batches.map(batch => {
                        const diagnosis = batch.diagnosis;
                        const severityColor = diagnosis?.severity === 'high' ? 'text-red-600' :
                          diagnosis?.severity === 'medium' ? 'text-yellow-600' : 'text-gray-600';
                        const diagnosisIcon = diagnosis?.type === 'orphaned' ? '🗑️' :
                          diagnosis?.type === 'duplicate' ? '🔄' :
                          diagnosis?.type === 'transient' ? '⚡' :
                          diagnosis?.type === 'billing' ? '💳' :
                          diagnosis?.type === 'missing_research' ? '🔬' : '❓';

                        return (
                          <TableRow key={batch.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedBatches.has(batch.id)}
                                onCheckedChange={() => toggleBatchSelection(batch.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-mono text-sm">#{batch.batch_number}</div>
                              <div className="text-xs text-muted-foreground">{batch.config_name}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span>{diagnosisIcon}</span>
                                <div>
                                  <div className={`font-medium ${severityColor}`}>
                                    {diagnosis?.label || 'Unknown'}
                                  </div>
                                  <div className="text-xs text-muted-foreground max-w-xs truncate" title={diagnosis?.description}>
                                    {diagnosis?.description || batch.error_message?.substring(0, 50)}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {diagnosis ? (
                                <div className="text-sm">
                                  <span className={diagnosis.existingProducts === 0 ? 'text-red-600' : ''}>
                                    {diagnosis.existingProducts}/{diagnosis.totalProducts}
                                  </span>
                                  <span className="text-muted-foreground text-xs ml-1">exist</span>
                                </div>
                              ) : (
                                batch.product_count
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={diagnosis?.action === 'cancel' ? 'secondary' :
                                  diagnosis?.action === 'reset' ? 'default' : 'outline'}
                                className="text-xs"
                              >
                                {diagnosis?.action === 'cancel' && '→ Cancel'}
                                {diagnosis?.action === 'reset' && '→ Retry'}
                                {diagnosis?.action === 'fix_research' && '→ Fix Research'}
                                {diagnosis?.action === 'investigate' && '→ Investigate'}
                                {!diagnosis?.action && '→ Unknown'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {diagnosis?.action === 'cancel' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs text-red-600"
                                  onClick={() => handleCancelBatches([batch.id])}
                                >
                                  Cancel
                                </Button>
                              )}
                              {diagnosis?.action === 'reset' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs text-green-600"
                                  onClick={() => handleResetBatches([batch.id])}
                                >
                                  Retry
                                </Button>
                              )}
                              {(diagnosis?.action === 'fix_research' || diagnosis?.action === 'investigate') && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 text-xs"
                                  onClick={() => {
                                    toggleBatchSelection(batch.id);
                                  }}
                                >
                                  Select
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Issues Alert (non-failed issues) */}
          {pipelineStatus.issues && pipelineStatus.issues.filter(i => i.type !== 'failed').length > 0 && (
            <Alert variant="destructive" className="border-amber-200 bg-amber-50 dark:bg-amber-950/30">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800 dark:text-amber-200">
                Pipeline Issues Detected
              </AlertTitle>
              <AlertDescription className="text-amber-700 dark:text-amber-300">
                {pipelineStatus.issues.filter(i => i.type !== 'failed').map((issue, idx) => (
                  <div key={idx} className="mt-1">
                    • {issue.message} ({issue.count} items)
                  </div>
                ))}
              </AlertDescription>
            </Alert>
          )}

          {/* Pipeline Overview - Funnel View */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Pipeline Overview
              </CardTitle>
              <CardDescription>
                {pipelineStatus.activeRunProducts ? (
                  <>
                    <span className="font-medium">{pipelineStatus.activeRunProducts.toLocaleString()}</span> products in active import
                    <span className="text-muted-foreground ml-2">
                      ({pipelineStatus.totalProducts.toLocaleString()} total in feed)
                    </span>
                  </>
                ) : (
                  <>{pipelineStatus.totalProducts.toLocaleString()} total products in feed</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* Pipeline Stages */}
                {(['enrichment', 'benefitScoring', 'embeddings', 'aeoGeneration'] as const).map((stage, index) => {
                  const config = STAGE_CONFIG[stage];
                  const stageData = pipelineStatus.stages[stage];
                  // Always use totalProducts for percentage calculation
                  // This shows site-wide completion status, not just active run
                  const total = pipelineStatus.totalProducts;
                  const percent = getProgressPercent(stageData.completed, total);
                  const isActive = stageData.inProgress > 0 || stageData.batches > 0;
                  const hasFailed = 'failed' in stageData && stageData.failed > 0;
                  const hasRateLimited = stage === 'embeddings' && (stageData.rateLimited ?? 0) > 0;

                  return (
                    <div key={stage}>
                      {/* Stage Row */}
                      <div className="flex items-center gap-4">
                        {/* Stage Icon */}
                        <div className={`flex-shrink-0 w-10 h-10 rounded-full ${config.lightBg} flex items-center justify-center ${config.color}`}>
                          {config.icon}
                        </div>

                        {/* Stage Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{config.label}</span>
                              {isActive && (
                                <Badge variant="secondary" className="text-xs">
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  {stageData.batches} batches
                                </Badge>
                              )}
                              {stageData.inProgress > 0 && (
                                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                  {stageData.inProgress.toLocaleString()} in progress
                                </Badge>
                              )}
                              {hasFailed && (
                                <Badge variant="destructive" className="text-xs">
                                  {stageData.failed?.toLocaleString()} failed
                                </Badge>
                              )}
                              {hasRateLimited && (
                                <Badge variant="outline" className="text-xs text-red-600 border-red-300">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  {stageData.rateLimited} rate limited
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm font-medium">
                              {stageData.completed.toLocaleString()} / {total.toLocaleString()}
                              <span className="text-muted-foreground ml-2">({percent}%)</span>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="relative">
                            <Progress value={percent} className="h-3" />
                            {/* In-progress indicator */}
                            {stageData.inProgress > 0 && (
                              <div
                                className={`absolute top-0 h-3 ${config.bgColor} opacity-50 animate-pulse rounded-full`}
                                style={{
                                  left: `${percent}%`,
                                  width: `${Math.min(getProgressPercent(stageData.inProgress, total), 100 - percent)}%`,
                                }}
                              />
                            )}
                          </div>

                          {/* Stage Description */}
                          <p className="text-xs text-muted-foreground mt-1">
                            {config.description}
                            {stageData.oldestStarted && isActive && (
                              <span className="ml-2 text-amber-600">
                                • Oldest batch started {formatDuration(stageData.oldestStarted)} ago
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Connector Arrow */}
                      {index < 3 && (
                        <div className="flex justify-center my-2">
                          <ArrowRight className="h-4 w-4 text-muted-foreground/30 rotate-90" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Stats */}
          {(() => {
            // Always use totalProducts for consistent site-wide percentages
            const total = pipelineStatus.totalProducts;
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">
                          {getProgressPercent(pipelineStatus.stages.enrichment.completed, total)}%
                        </div>
                        <div className="text-sm text-muted-foreground">Enriched</div>
                        <div className="text-xs text-muted-foreground">
                          {pipelineStatus.stages.enrichment.completed.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center">
                        <Users className="h-5 w-5 text-pink-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">
                          {getProgressPercent(pipelineStatus.stages.benefitScoring.completed, total)}%
                        </div>
                        <div className="text-sm text-muted-foreground">Scored</div>
                        <div className="text-xs text-muted-foreground">
                          {pipelineStatus.stages.benefitScoring.completed.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                        <Database className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">
                          {getProgressPercent(pipelineStatus.stages.embeddings.completed, total)}%
                        </div>
                        <div className="text-sm text-muted-foreground">Embedded</div>
                        <div className="text-xs text-muted-foreground">
                          {pipelineStatus.stages.embeddings.completed.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                        <FileText className="h-5 w-5 text-violet-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold">
                          {getProgressPercent(pipelineStatus.stages.aeoGeneration.completed, total)}%
                        </div>
                        <div className="text-sm text-muted-foreground">AEO Ready</div>
                        <div className="text-xs text-muted-foreground">
                          {pipelineStatus.stages.aeoGeneration.completed.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Current Processing Details */}
          {(pipelineStatus.stages.enrichment.batches > 0 ||
            pipelineStatus.stages.benefitScoring.batches > 0 ||
            pipelineStatus.stages.embeddings.batches > 0 ||
            pipelineStatus.stages.aeoGeneration.batches > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Currently Processing
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pipelineStatus.stages.enrichment.batches > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                      <Sparkles className="h-5 w-5 text-orange-600" />
                      <div>
                        <div className="font-medium">Enrichment</div>
                        <div className="text-sm text-muted-foreground">
                          {pipelineStatus.stages.enrichment.batches} batches ·{' '}
                          {pipelineStatus.stages.enrichment.inProgress.toLocaleString()} products
                        </div>
                      </div>
                    </div>
                  )}

                  {pipelineStatus.stages.benefitScoring.batches > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-pink-50 dark:bg-pink-900/20">
                      <Users className="h-5 w-5 text-pink-600" />
                      <div>
                        <div className="font-medium">Benefit Scoring</div>
                        <div className="text-sm text-muted-foreground">
                          {pipelineStatus.stages.benefitScoring.batches} batches ·{' '}
                          {pipelineStatus.stages.benefitScoring.inProgress.toLocaleString()} products
                        </div>
                      </div>
                    </div>
                  )}

                  {pipelineStatus.stages.embeddings.batches > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-cyan-50 dark:bg-cyan-900/20">
                      <Database className="h-5 w-5 text-cyan-600" />
                      <div>
                        <div className="font-medium">Embeddings</div>
                        <div className="text-sm text-muted-foreground">
                          {pipelineStatus.stages.embeddings.batches} batches ·{' '}
                          {pipelineStatus.stages.embeddings.inProgress.toLocaleString()} products
                        </div>
                      </div>
                    </div>
                  )}

                  {pipelineStatus.stages.aeoGeneration.batches > 0 && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-50 dark:bg-violet-900/20">
                      <FileText className="h-5 w-5 text-violet-600" />
                      <div>
                        <div className="font-medium">AEO Generation</div>
                        <div className="text-sm text-muted-foreground">
                          {pipelineStatus.stages.aeoGeneration.batches} batches ·{' '}
                          {pipelineStatus.stages.aeoGeneration.inProgress.toLocaleString()} products
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No pipeline data available. Start a feed import to see processing status.
          </AlertDescription>
        </Alert>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Failed Batches</DialogTitle>
            <DialogDescription>
              This will reset {selectedBatches.size} failed batch{selectedBatches.size !== 1 ? 'es' : ''} to retry processing.
              The batches will be picked up by the scheduler and re-processed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={() => handleResetFailed(false)} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reset {selectedBatches.size} Batch{selectedBatches.size !== 1 ? 'es' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Batches</DialogTitle>
            <DialogDescription>
              This will cancel {selectedBatches.size} batch{selectedBatches.size !== 1 ? 'es' : ''} and any associated OpenAI jobs.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}>
              Keep Running
            </Button>
            <Button variant="destructive" onClick={handleCancelBatches} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <StopCircle className="h-4 w-4 mr-2" />}
              Cancel Batches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
